import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Box, Button, Tooltip } from "@mui/material";
import { formFields, getColumns } from "./columns";
import { useLazyGetArmListQuery } from "../../../middleware/usersApi";
import { EnhancedTableHead } from "../ObjectsList/TableBasic/Filters";
import { getAddressString, getTableHieght } from "../../../helpers/tableHelper";
import useScreenSize from "../../../hooks/useScreenSize";
import TableBasic from "../ObjectsList/TableBasic";
import ClearFilterIcon from "../../components/icons/ButtonIcons/ClearFilterIcon";
import { TooltipProps } from "../../../helpers/materialUITheme";

const ARMSTable = () => {
  const armsStorageName = "armsListSearchParams";

  const { searchUserNameStorage, searchSubdivisionAddressStorage, searchSubdivisionNameStorage } =
    JSON.parse(sessionStorage.getItem(armsStorageName)) || {};
  const screenSize = useScreenSize();
  const [pageNumber, setPageNumber] = useState(0);
  const [getArmList, { data: armList, isLoading }] = useLazyGetArmListQuery();
  const intervalRef = useRef(null);

  const { control, reset } = useForm({
    defaultValues: {
      [formFields.searchParams]: {
        [formFields.userName]: searchUserNameStorage || "",
        [formFields.subdivisionName]: searchSubdivisionNameStorage || "",
        [formFields.subdivisionAddress]: searchSubdivisionAddressStorage || "",
      },
    },
  });

  function writeSearchDataToSessionStorage(newSearchParam, searchValue) {
    const getStorage = JSON.parse(sessionStorage.getItem(armsStorageName)) || {};
    const searchParamsForLocalStorage = {
      ...getStorage,
      [newSearchParam]: searchValue,
    };
    sessionStorage.setItem(armsStorageName, JSON.stringify(searchParamsForLocalStorage));
  }

  function savePageNumberToSessionStorage(currentPageNumber) {
    const getStorage = JSON.parse(sessionStorage.getItem(armsStorageName)) || {};
    const searchParamsForLocalStorage = {
      ...getStorage,
      pageNumberStorage: currentPageNumber,
    };
    sessionStorage.setItem(armsStorageName, JSON.stringify(searchParamsForLocalStorage));
  }

  const params = useWatch({
    control,
    name: formFields.searchParams,
  });

  const getArmsTableData = (isSetFirstPage = false) => {
    isSetFirstPage && setPageNumber(0);
    const sessionStorageData = JSON.parse(sessionStorage.getItem(armsStorageName)) || {};

    const preparedData = {
      userName: params.userName || sessionStorageData.searchUserNameStorage || "",
      subdivisionName: params.subdivisionName || sessionStorageData.searchSubdivisionNameStorage || "",
      address: getAddressString(params?.subdivisionAddress || sessionStorageData.searchSubdivisionAddressStorage, "SubdivisionAddressFilter"),
      pageNumber,
    };

    getArmList(preparedData);
  };

  function clearFiltersHandler() {
    setPageNumber(0);
    reset({
      [formFields.searchParams]: {
        [formFields.userName]: "",
        [formFields.subdivisionName]: "",
        [formFields.subdivisionAddress]: "",
      },
    });
    sessionStorage.removeItem(armsStorageName);
  }

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    getArmsTableData();
    intervalRef.current = setInterval(getArmsTableData, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!params) return;
    getArmsTableData(true);
  }, [params]);

  useEffect(() => {
    if (!params) return;
    getArmsTableData(false);
  }, [pageNumber]);

  const rows = useMemo(() => {
    const { data } = armList || {};
    return data?.length
      ? data.map(({ userName, subdivisionAddress, subdivisionName, controlConsoles }) => {
          return {
            userName,
            subdivisionAddress,
            subdivisionName,
            controlConsoles,
          };
        })
      : [];
  }, [armList]);

  const columns = useMemo(
    () =>
      getColumns({
        control,
        writeSearchDataToSessionStorage,
      }),
    [params]
  );

  const renderHeader = useMemo(() => <EnhancedTableHead columns={columns} />, [columns]);
  const recordsCount = useMemo(() => armList && armList?.recordsCount, [armList]);

  return (
    <>
      <Box className="title-page" sx={{ height: "42px", display: "flex", alignItems: "center" }}>
        Список АРМов
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Tooltip title="Очистить все фильтры" componentsProps={TooltipProps} placement="bottom" arrow>
          <Button className="btn btn-white" onClick={clearFiltersHandler}>
            <ClearFilterIcon />
          </Button>
        </Tooltip>
      </Box>

      <TableBasic
        renderHeader={renderHeader}
        columns={columns}
        rows={rows}
        handleClickCell={() => {}}
        withSearchField={false}
        isLoading={isLoading}
        pageNumber={pageNumber}
        changePageNumber={setPageNumber}
        count={recordsCount}
        minHeight={getTableHieght(screenSize, false, "390px").min}
        maxHeight={getTableHieght(screenSize, false, "390px").max}
        savePageNumberToSessionStorage={savePageNumberToSessionStorage}
      />
    </>
  );
};

export default ARMSTable;
